import type { ReactNode } from 'react';

import Button from '@/components/Button/Button';
import Skeleton from '@/components/Skeleton/Skeleton';

interface AsyncSectionProps {
  loading: boolean;
  error: boolean;
  errorMessage?: string;
  onRetry: () => void;
  /** Skeleton scaffold rendered while loading. */
  loadingSkeleton?: ReactNode;
  children: ReactNode;
}

const defaultSkeleton = (
  <div className="space-y-3">
    <Skeleton height={16} width="40%" />
    <Skeleton height={36} />
    <Skeleton height={120} />
  </div>
);

/**
 * Shared loading / error / content scaffold for home-automation settings
 * sections. Keeps the three sections (homes, ingestion, unknown) visually
 * consistent and centralizes the retry pattern.
 */
export default function AsyncSection({
  loading,
  error,
  errorMessage = 'Failed to load.',
  onRetry,
  loadingSkeleton,
  children,
}: AsyncSectionProps) {
  if (loading) return <>{loadingSkeleton ?? defaultSkeleton}</>;
  if (error) {
    return (
      <div className="space-y-3 rounded-lg border border-(--border-subtle) bg-(--bg-surface) px-4 py-4">
        <p className="text-sm text-(--accent-danger)">{errorMessage}</p>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  return <>{children}</>;
}
